import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CircularProgress from "@mui/material/CircularProgress";
import BulkEditTagsDialog from "common/Components/BulkEditTagsDialog";
import FilterablePaginatedTable from "common/Components/FilterablePaginatedTable";
import TablePageAlerts from "common/Components/TablePageAlerts";
import TagDetailsDrawer from "./TagDetailsDrawer";
import UserContext from "common/Context/UserContext";
import { useEffectiveAccess } from "common/Context/EffectiveAccessContext";
import { hasDataManagementAccess } from "common/Utilities/EffectiveAccess.utils";
import {
  isFiniteNumber,
  isNullUndefinedOrEmptyString,
} from "common/utils/valueChecks";
import { validateNodeNameForSave } from "common/utils/nodeNameValidation";
import {
  TagsAliasPathText,
  TagsLoadingLabel,
  TagsLoadingRow,
  TagsPageRoot,
  TagsTableFrame,
  TagsReadOnlyCellText,
  ToolbarClearFiltersButton,
} from "./Tags.styles";
import {
  buildPatchBodyForBulkUpdate,
  buildNodeTypeOptions,
  buildSubtypeOptionsByNodeType,
  buildTagSubtypeOptions,
  buildUnifiedContextualizeTagsFromRows,
  collectUnionOfAllowedParentTypes,
  mapTagSummaryToRow,
  parentNodeFromContextPath,
  mergeParentCandidatesFromTagRows,
  isTagsBulkEditSelectionAllowed,
  canBulkContextualizeTagRow,
} from "./tagRowsMapper";
import {
  BULK_CONTEXTUALIZE_MAX_TAGS,
  bulkContextualizeUnifiedTags,
  exportTagsCsv,
  extractPagedNodesList,
  extractTotalRowCountFromPagedPayload,
  fetchNodeTypesReference,
  fetchParentCandidates,
  fetchTagsPage,
  patchContextNodes,
  resolveContextNodeIdForTagRowAsync,
} from "./tagsApi";
import { TAGS_UI } from "./Tags.constants";

const BULK_EDIT_BLOCKED_TOOLTIP =
  "Bulk edit: choose contextual rows that share the same node type, or only uncontextualized rows to contextualize together.";

const TAGS_EXPORT_FILENAME = "tags.csv";
const FILTER_SELECT_ALL_SENTINEL = "__all__";
const DEFAULT_SORT = "displayName,asc";

const SORT_COLUMN_TO_API = {
  sourceSystem: "source",
  nodeName: "displayName",
  edgeTag: "canonicalTagId",
  nodeType: "nodeType",
  parentNode: "context",
};

function readOnlyText(value) {
  return (
    <TagsReadOnlyCellText variant="body2" title={value != null ? String(value) : ""}>
      {isNullUndefinedOrEmptyString(value) ? "—" : String(value)}
    </TagsReadOnlyCellText>
  );
}

function aliasPathCell(row) {
  const derived =
    row?.sourceTagId ||
    row?.context ||
    (row?.uncontextualized ? "(uncontextualized)" : "—");
  return (
    <TagsAliasPathText
      variant="body2"
      $contextualized={Boolean(row?.sourceTagId || row?.context)}
      title={derived}
    >
      {derived}
    </TagsAliasPathText>
  );
}

function buildTagColumns({
  nodeTypeOptions,
  allSubtypeOptions,
  customerName,
}) {
  return [
    {
      id: "sourceSystem",
      label: "Source Connector",
      sortable: true,
      filter: { type: "text" },
      render: (v) => readOnlyText(v),
    },
    {
      id: "nodeName",
      label: "Node Name",
      sortable: true,
      filter: { type: "text" },
      render: (v) => readOnlyText(v),
    },
    {
      id: "edgeTag",
      label: "Source Path",
      sortable: true,
      minWidth: 220,
      filter: { type: "text" },
      render: (v) => readOnlyText(v),
    },
    {
      id: "nodeType",
      label: "Node Type",
      sortable: true,
      filter:
        nodeTypeOptions.length > 0
          ? { type: "select", options: nodeTypeOptions }
          : { type: "text" },
      render: (v) => readOnlyText(v),
    },
    {
      id: "nodeSubType",
      label: "Node Subtype",
      sortable: true,
      filter:
        allSubtypeOptions.length > 0
          ? { type: "select", options: allSubtypeOptions }
          : { type: "text" },
      render: (v) => readOnlyText(v),
    },
    {
      id: "parentNode",
      label: "Parent Node",
      sortable: true,
      minWidth: 180,
      filter: { type: "text" },
      render: (_cellValue, row) => {
        if (row?.uncontextualized) {
          return readOnlyText("");
        }
        return readOnlyText(
          parentNodeFromContextPath(row?.context, customerName)
        );
      },
    },
    {
      id: "sourceTagId",
      label: "Alias Path",
      sortable: true,
      minWidth: 220,
      filter: { type: "text" },
      render: (_v, row) => aliasPathCell(row),
    },
  ];
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function formatApiError(result) {
  if (typeof result.error === "string" && result.error.trim()) {
    return result.error;
  }
  const data = result.data;
  if (data && typeof data === "object" && data.success === false) {
    const failed = data.results?.find((r) => r.status !== "updated");
    if (failed?.error) return failed.error;
  }
  return "Request failed. Please try again.";
}

function formatBulkContextualizeSummary(data) {
  if (!data || typeof data !== "object") {
    return "Bulk contextualize completed with an unknown response.";
  }
  const created = data.created ?? 0;
  const failed = data.failed ?? 0;
  if (data.success === true) {
    return `Successfully contextualized ${created} tag(s).`;
  }
  const parts = [`Created ${created}, failed ${failed}.`];
  if (Array.isArray(data.results)) {
    for (const r of data.results) {
      if (String(r?.status) === "failed") {
        const tagKey =
          r.canonical_tag_id ??
          r.canonicalTagId ??
          r.sourceMetricId ??
          "tag";
        parts.push(`${r.source ?? ""} ${tagKey}: ${r.error ?? "Failed"}`);
      }
    }
  }
  return parts.join(" ");
}

function toNonEmptyString(value) {
  if (value == null) return "";
  return String(value).trim();
}

function hasActiveTagFilters(filters) {
  return Object.values(filters ?? {}).some((value) => {
    const text = toNonEmptyString(value);
    return text !== "" && text !== FILTER_SELECT_ALL_SENTINEL;
  });
}

function resolveParentNodeNameFilter(parentFilter) {
  const parentFilterString = toNonEmptyString(parentFilter);
  if (parentFilterString === "" || parentFilter === FILTER_SELECT_ALL_SENTINEL) {
    return "";
  }
  return parentFilterString;
}

function buildTagListApiQuery(filters, page, size, sort) {
  const parentNodeName = resolveParentNodeNameFilter(filters.parentNode);

  return {
    page,
    size,
    sort,
    ...(toNonEmptyString(filters.nodeName) !== ""
      ? { nodeName: filters.nodeName }
      : {}),
    ...(toNonEmptyString(filters.sourceSystem) !== ""
      ? { sourceConnector: filters.sourceSystem }
      : {}),
    ...(toNonEmptyString(filters.edgeTag) !== ""
      ? { sourcePath: filters.edgeTag }
      : {}),
    ...(toNonEmptyString(filters.sourceTagId) !== ""
      ? { sourceTagId: filters.sourceTagId }
      : {}),
    ...(toNonEmptyString(filters.nodeType) !== "" &&
    filters.nodeType !== FILTER_SELECT_ALL_SENTINEL
      ? { nodeType: filters.nodeType }
      : {}),
    ...(toNonEmptyString(filters.nodeSubType) !== "" &&
    filters.nodeSubType !== FILTER_SELECT_ALL_SENTINEL
      ? { nodeSubType: filters.nodeSubType }
      : {}),
    ...(parentNodeName !== "" ? { parentNodeName } : {}),
  };
}

export default function Tags() {
  const { userProfile, getCustomerName, getCustomerCode } = useContext(UserContext);
  const customerName = getCustomerName?.() ?? "";
  const profile = userProfile?.profile;
  const { canAccessPage, usesEffectiveAccess } = useEffectiveAccess();
  const canAccessDataManagement = hasDataManagementAccess(profile, {
    usesEffectiveAccess,
    canAccessPage,
  });
  const tagsTableRemountKey = String(getCustomerCode?.() ?? "none");

  const [rows, setRows] = useState([]);
  const [tagSubtypeOptions, setTagSubtypeOptions] = useState([]);
  const [nodeTypeOptions, setNodeTypeOptions] = useState([]);
  const [subtypeOptionsByNodeType, setSubtypeOptionsByNodeType] = useState({});
  const [referencePayload, setReferencePayload] = useState(null);
  const [parentCandidates, setParentCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortValue, setSortValue] = useState(DEFAULT_SORT);
  const [totalRows, setTotalRows] = useState(0);

  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditRows, setBulkEditRows] = useState([]);
  const [tagDetailsRow, setTagDetailsRow] = useState(null);
  const [tagDetailsSaving, setTagDetailsSaving] = useState(false);
  const [tableFilters, setTableFilters] = useState({});
  const activeFiltersRef = useRef({});
  const parentCatalogFetchedRef = useRef(false);
  const getCustomerCodeRef = useRef(getCustomerCode);

  useEffect(() => {
    getCustomerCodeRef.current = getCustomerCode;
  }, [getCustomerCode]);

  const tagColumns = useMemo(
    () =>
      buildTagColumns({
        nodeTypeOptions,
        allSubtypeOptions: tagSubtypeOptions,
        customerName,
      }),
    [nodeTypeOptions, tagSubtypeOptions, customerName]
  );

  const loadReferenceData = useCallback(async () => {
    const referenceResult = await fetchNodeTypesReference();
    if (referenceResult.data && !referenceResult.error) {
      const data = referenceResult.data;
      setReferencePayload(data);
      setTagSubtypeOptions(buildTagSubtypeOptions(data));
      setNodeTypeOptions(buildNodeTypeOptions(data));
      setSubtypeOptionsByNodeType(buildSubtypeOptionsByNodeType(data));
      return data;
    }
    setReferencePayload(null);
    return null;
  }, []);

  const loadTagRows = useCallback(
    async (filters = {}, page = 0, size = 25, sort = DEFAULT_SORT) => {
      setLoadError(null);
      const customerCode = getCustomerCodeRef.current?.();
      if (!customerCode) {
        setLoading(false);
        setLoadError("No tenant selected. Sign in or pick a customer.");
        setRows([]);
        setTotalRows(0);
        return null;
      }

      const apiQuery = buildTagListApiQuery(
        filters,
        page,
        size,
        sort
      );

      setLoading(true);
      const tagsResult = await fetchTagsPage(apiQuery);

      if (tagsResult.error || tagsResult.status >= 400) {
        setLoadError(formatApiError(tagsResult));
        setRows([]);
        setTotalRows(0);
      } else {
        const payload = tagsResult.data ?? {};
        const list = extractPagedNodesList(payload);
        const mappedRows = list.map(mapTagSummaryToRow);
        setRows(mappedRows);

        const totalFromApi = extractTotalRowCountFromPagedPayload(payload);
        setTotalRows(
          totalFromApi != null ? totalFromApi : mappedRows.length
        );
        setLoading(false);
        return mappedRows;
      }
      setLoading(false);
      return null;
    },
    []
  );

  const refreshTagsForCustomerChange = useCallback(async () => {
    parentCatalogFetchedRef.current = false;
    activeFiltersRef.current = {};
    setTableFilters({});
    setPageIndex(0);
    setSortValue(DEFAULT_SORT);
    setBulkEditOpen(false);
    setBulkEditRows([]);
    setActionError(null);
    setActionSuccess(null);
    setLoadError(null);

    const customerCode = getCustomerCodeRef.current?.();
    if (!customerCode) {
      setRows([]);
      setTotalRows(0);
      setLoading(false);
      setLoadError("No tenant selected. Sign in or pick a customer.");
      setParentCandidates([]);
      return;
    }

    const refPromise = loadReferenceData();
    const tagsPromise = loadTagRows(
      activeFiltersRef.current,
      0,
      rowsPerPage,
      DEFAULT_SORT
    );
    const [ref, mappedRows] = await Promise.all([refPromise, tagsPromise]);
    if (!ref?.nodeTypes) {
      setParentCandidates([]);
      return;
    }
    const contextualOnly = (mappedRows ?? []).filter((r) => !r.uncontextualized);
    setParentCandidates(mergeParentCandidatesFromTagRows(contextualOnly, ref));
  }, [loadReferenceData, loadTagRows, rowsPerPage]);

  useEffect(() => {
    const handleCustomerChange = () => {
      refreshTagsForCustomerChange();
    };
    window.addEventListener("customerChanged", handleCustomerChange);
    return () =>
      window.removeEventListener("customerChanged", handleCustomerChange);
  }, [refreshTagsForCustomerChange]);

  const INITIAL_PAGE_SIZE = 25;

  useEffect(() => {
    let cancelled = false;
    parentCatalogFetchedRef.current = false;
    (async () => {
      const refPromise = loadReferenceData();
      const tagsPromise = loadTagRows(
        activeFiltersRef.current,
        0,
        INITIAL_PAGE_SIZE,
        DEFAULT_SORT
      );
      const [ref, mappedRows] = await Promise.all([refPromise, tagsPromise]);
      if (cancelled) return;
      if (!ref?.nodeTypes) {
        setParentCandidates([]);
        return;
      }
      const contextualOnly = (mappedRows ?? []).filter((r) => !r.uncontextualized);
      setParentCandidates(mergeParentCandidatesFromTagRows(contextualOnly, ref));
    })();
    return () => {
      cancelled = true;
    };
  }, [loadReferenceData, loadTagRows]);

  useEffect(() => {
    if ((!bulkEditOpen && !tagDetailsRow) || !referencePayload?.nodeTypes) {
      return undefined;
    }
    if (parentCatalogFetchedRef.current) return undefined;
    let cancelled = false;
    (async () => {
      const types = collectUnionOfAllowedParentTypes(referencePayload);
      const result = await fetchParentCandidates(types);
      if (cancelled) return;
      if (result.error || !Array.isArray(result.data)) return;
      parentCatalogFetchedRef.current = true;
      setParentCandidates((previous) => {
        const byId = new Map(previous.map((c) => [String(c.nodeId), c]));
        for (const c of result.data) {
          byId.set(String(c.nodeId), c);
        }
        return [...byId.values()];
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [bulkEditOpen, tagDetailsRow, referencePayload]);

  const handleTagRowClick = useCallback((row) => {
    setTagDetailsRow(row);
  }, []);

  const handleTagDetailsClose = useCallback(() => {
    setTagDetailsRow(null);
  }, []);

  const contextualizeSingleRow = useCallback(async (row, patch) => {
    const tags = buildUnifiedContextualizeTagsFromRows([row]);
    if (!tags.length) {
      setActionError(
        "This row cannot be contextualized (missing source or source path)."
      );
      return false;
    }
    const body = { tags };
    const nodeSubType = toNonEmptyString(patch?.nodeSubType);
    if (nodeSubType) body.nodeSubType = nodeSubType;
    const nodeName = toNonEmptyString(patch?.nodeName);
    if (nodeName) {
      const nodeNameError = validateNodeNameForSave(nodeName);
      if (nodeNameError) {
        setActionError(nodeNameError);
        return false;
      }
      body.nodeName = nodeName;
    } else {
      setActionError("Node name is required to contextualize the row.");
      return false;
    }
    if (patch?.parentNodeId != null && patch.parentNodeId !== "") {
      const parsedParentId = Number(patch.parentNodeId);
      if (isFiniteNumber(parsedParentId)) {
        body.parentNodeId = parsedParentId;
      }
    }
    const bulkResult = await bulkContextualizeUnifiedTags(body);
    if (bulkResult.error || (bulkResult.status != null && bulkResult.status >= 400)) {
      setActionError(formatApiError(bulkResult));
      return false;
    }
    const payload = bulkResult.data;
    if (
      bulkResult.status === 207 ||
      (payload && typeof payload === "object" && payload.success === false)
    ) {
      setActionError(formatBulkContextualizeSummary(payload));
      return false;
    }
    return true;
  }, []);

  const handleTagDetailsSave = useCallback(
    async (patch, contextNodeIdFromDrawer) => {
      if (!tagDetailsRow) return;
      setTagDetailsSaving(true);
      setActionError(null);
      setActionSuccess(null);
      try {
        if (tagDetailsRow.uncontextualized) {
          const ok = await contextualizeSingleRow(tagDetailsRow, patch);
          if (!ok) return;
          setActionSuccess("Tag contextualized successfully.");
        } else {
          let contextNodeId = contextNodeIdFromDrawer;
          if (contextNodeId == null) {
            contextNodeId = await resolveContextNodeIdForTagRowAsync(
              tagDetailsRow
            );
          }
          if (contextNodeId == null) {
            setActionError(
              "Could not resolve context node id for this row."
            );
            return;
          }
          if (patch?.nodeName != null) {
            const nodeNameError = validateNodeNameForSave(patch.nodeName);
            if (nodeNameError) {
              setActionError(nodeNameError);
              return;
            }
          }
          const body = buildPatchBodyForBulkUpdate([contextNodeId], patch);
          const result = await patchContextNodes(body);
          if (result.error) {
            setActionError(formatApiError(result));
            return;
          }
          if (result.data?.success === false) {
            setActionError(formatApiError(result));
            return;
          }
          setActionSuccess("Tag updated successfully.");
        }
        handleTagDetailsClose();
        await loadTagRows(
          activeFiltersRef.current,
          pageIndex,
          rowsPerPage,
          sortValue
        );
      } finally {
        setTagDetailsSaving(false);
      }
    },
    [
      tagDetailsRow,
      handleTagDetailsClose,
      loadTagRows,
      pageIndex,
      rowsPerPage,
      sortValue,
      contextualizeSingleRow,
    ]
  );

  const getRowId = useCallback(
    (row) => row.rowKey ?? String(row.id ?? ""),
    []
  );

  const isBulkEditAllowed = useCallback((selectedRows) => {
    if (!isTagsBulkEditSelectionAllowed(selectedRows)) return false;
    const allUnctx =
      selectedRows.length > 0 &&
      selectedRows.every((r) => r.uncontextualized);
    if (allUnctx) {
      return selectedRows.every(canBulkContextualizeTagRow);
    }
    return true;
  }, []);

  const bulkEditLabel = useCallback((selectedCount, selectedRows) => {
    if (
      selectedCount >= 1 &&
      Array.isArray(selectedRows) &&
      selectedRows.length > 0 &&
      selectedRows.every((r) => r.uncontextualized)
    ) {
      return selectedCount === 1
        ? "Contextualize Node"
        : `Contextualize (${selectedCount})`;
    }
    return `Bulk edit (${selectedCount})`;
  }, []);

  const handleBulkEdit = useCallback((selectedRows) => {
    setBulkEditRows(selectedRows);
    setBulkEditOpen(true);
  }, []);

  const handleBulkEditClose = useCallback(() => {
    setBulkEditOpen(false);
    setBulkEditRows([]);
  }, []);

  const handleBulkApply = useCallback(
    async (patch) => {
      setActionError(null);
      setActionSuccess(null);
      const isAllUncontextualizedSelection =
        bulkEditRows.length > 0 && bulkEditRows.every((r) => r.uncontextualized);
      if (isAllUncontextualizedSelection) {
        const tags = buildUnifiedContextualizeTagsFromRows(bulkEditRows);
        if (!tags.length) {
          setActionError("No valid unmapped tags in the selection.");
          return;
        }
        if (tags.length > BULK_CONTEXTUALIZE_MAX_TAGS) {
          setActionError(
            `At most ${BULK_CONTEXTUALIZE_MAX_TAGS} tags can be contextualized at once (selected ${tags.length}).`
          );
          return;
        }
        const body = { tags };
        const nodeSubType = toNonEmptyString(patch?.nodeSubType);
        if (nodeSubType) body.nodeSubType = nodeSubType;
        const nodeName = toNonEmptyString(patch?.nodeName);
        if (nodeName) {
          const nodeNameError = validateNodeNameForSave(nodeName);
          if (nodeNameError) {
            setActionError(nodeNameError);
            return;
          }
          body.nodeName = nodeName;
        }
        if (patch?.parentNodeId != null && patch.parentNodeId !== "") {
          const parsedParentId = Number(patch.parentNodeId);
          if (isFiniteNumber(parsedParentId)) {
            body.parentNodeId = parsedParentId;
          }
        }

        const bulkResult = await bulkContextualizeUnifiedTags(body);
        if (bulkResult.error || (bulkResult.status != null && bulkResult.status >= 400)) {
          setActionError(formatApiError(bulkResult));
          return;
        }
        const payload = bulkResult.data;
        if (
          bulkResult.status === 207 ||
          (payload && typeof payload === "object" && payload.success === false)
        ) {
          setActionError(formatBulkContextualizeSummary(payload));
        } else {
          setActionSuccess(formatBulkContextualizeSummary(payload));
        }
        handleBulkEditClose();
        await loadTagRows(
          activeFiltersRef.current,
          pageIndex,
          rowsPerPage,
          sortValue
        );
        return;
      }

      const contextNodeIds = [];
      for (const row of bulkEditRows) {
        if (row.uncontextualized) continue;
        const contextNodeId = await resolveContextNodeIdForTagRowAsync(row);
        if (contextNodeId == null) {
          setActionError("Could not resolve context node ids for the selection.");
          return;
        }
        contextNodeIds.push(contextNodeId);
      }
      if (!contextNodeIds.length) {
        setActionError("Could not resolve context node ids for the selection.");
        return;
      }

      if (patch?.nodeName != null) {
        const nodeNameError = validateNodeNameForSave(patch.nodeName);
        if (nodeNameError) {
          setActionError(nodeNameError);
          return;
        }
      }

      const body = buildPatchBodyForBulkUpdate(contextNodeIds, patch);
      const result = await patchContextNodes(body);
      if (result.error) {
        setActionError(formatApiError(result));
        return;
      }
      if (result.data?.success === false) {
        setActionError(formatApiError(result));
        return;
      }
      setActionSuccess(
        `Updated ${contextNodeIds.length} tag(s) successfully.`
      );
      handleBulkEditClose();
      await loadTagRows(
        activeFiltersRef.current,
        pageIndex,
        rowsPerPage,
        sortValue
      );
    },
    [bulkEditRows, loadTagRows, pageIndex, rowsPerPage, sortValue, handleBulkEditClose]
  );

  const handleTagsExport = useCallback(async () => {
    setActionError(null);
    setActionSuccess(null);
    setExportingCsv(true);
    try {
      const result = await exportTagsCsv({
        tableFilters: activeFiltersRef.current,
        sort: sortValue,
      });
      if (result.error || !(result.data instanceof Blob)) {
        setActionError(
          typeof result.error === "string" && result.error.trim()
            ? result.error
            : "Export failed."
        );
        return;
      }
      triggerBlobDownload(result.data, TAGS_EXPORT_FILENAME);
    } finally {
      setExportingCsv(false);
    }
  }, [sortValue]);

  const tablePageAlertItems = useMemo(() => {
    const out = [];
    if (loadError) {
      out.push({
        key: "load",
        severity: "error",
        message: loadError,
        onClose: () => setLoadError(null),
      });
    }
    if (actionError) {
      out.push({
        key: "action",
        severity: "warning",
        message: actionError,
        onClose: () => setActionError(null),
      });
    }
    if (actionSuccess) {
      out.push({
        key: "action-success",
        severity: "success",
        message: actionSuccess,
        onClose: () => setActionSuccess(null),
      });
    }
    return out;
  }, [loadError, actionError, actionSuccess]);

  const handleTableFilterChange = useCallback(
    (nextFilters) => {
      activeFiltersRef.current = nextFilters;
      setTableFilters(nextFilters);
      setPageIndex(0);
      loadTagRows(nextFilters, 0, rowsPerPage, sortValue);
    },
    [loadTagRows, rowsPerPage, sortValue]
  );

  const handleClearFilters = useCallback(() => {
    activeFiltersRef.current = {};
    setTableFilters({});
    setPageIndex(0);
    loadTagRows({}, 0, rowsPerPage, sortValue);
  }, [loadTagRows, rowsPerPage, sortValue]);

  const filtersActive = hasActiveTagFilters(tableFilters);

  const handlePageChange = useCallback(
    (nextPage) => {
      setPageIndex(nextPage);
      loadTagRows(activeFiltersRef.current, nextPage, rowsPerPage, sortValue);
    },
    [loadTagRows, rowsPerPage, sortValue]
  );

  const handleRowsPerPageChange = useCallback(
    (nextRowsPerPage) => {
      setRowsPerPage(nextRowsPerPage);
      setPageIndex(0);
      loadTagRows(activeFiltersRef.current, 0, nextRowsPerPage, sortValue);
    },
    [loadTagRows, sortValue]
  );

  const handleSortChange = useCallback(
    (columnId, sortOrder) => {
      const apiSortField = SORT_COLUMN_TO_API[columnId] ?? columnId;
      const nextSortValue = `${apiSortField},${sortOrder}`;
      setSortValue(nextSortValue);
      setPageIndex(0);
      loadTagRows(activeFiltersRef.current, 0, rowsPerPage, nextSortValue);
    },
    [loadTagRows, rowsPerPage]
  );

  return (
    <TagsPageRoot data-testid="tags-page">
      <TablePageAlerts items={tablePageAlertItems} />

      <TagDetailsDrawer
        open={Boolean(tagDetailsRow)}
        row={tagDetailsRow}
        onClose={handleTagDetailsClose}
        onSave={handleTagDetailsSave}
        saving={tagDetailsSaving}
        nodeTypeOptions={nodeTypeOptions}
        subtypeOptionsByNodeType={subtypeOptionsByNodeType}
        referencePayload={referencePayload}
        parentCandidates={parentCandidates}
      />

      <BulkEditTagsDialog
        open={bulkEditOpen}
        onClose={handleBulkEditClose}
        selectedRows={bulkEditRows}
        onApply={handleBulkApply}
        nodeTypeOptions={nodeTypeOptions}
        subtypeOptionsByNodeType={subtypeOptionsByNodeType}
        referencePayload={referencePayload}
        parentCandidates={parentCandidates}
      />

      {loading ? (
        <TagsLoadingRow>
          <CircularProgress size={18} aria-label="Loading tag rows" />
          <TagsLoadingLabel variant="body2">Loading rows...</TagsLoadingLabel>
        </TagsLoadingRow>
      ) : null}
      <TagsTableFrame>
        <FilterablePaginatedTable
          key={tagsTableRemountKey}
          title="Tags"
          columns={tagColumns}
          rows={rows}
          serverMode
          totalRows={totalRows}
          page={pageIndex}
          getRowId={getRowId}
          defaultRowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100, 250]}
          selectable
          onBulkEdit={handleBulkEdit}
          bulkEditLabel={bulkEditLabel}
          isBulkEditAllowed={isBulkEditAllowed}
          bulkEditBlockedTooltip={BULK_EDIT_BLOCKED_TOOLTIP}
          showExport={canAccessDataManagement}
          onExportCsv={handleTagsExport}
          exportFileName={TAGS_EXPORT_FILENAME}
          exportDisabled={loading || exportingCsv}
          exportDisabledTitle={loading ? "Loading tags…" : ""}
          exportLoading={exportingCsv}
          onRowClick={handleTagRowClick}
          onFilterChange={handleTableFilterChange}
          filterValues={tableFilters}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortChange}
          applyTextFiltersOnCommit
          toolbarActions={
            <ToolbarClearFiltersButton
              variant="outlined"
              disabled={!filtersActive}
              onClick={handleClearFilters}
            >
              {TAGS_UI.clearFiltersLabel}
            </ToolbarClearFiltersButton>
          }
          testId="tags-table"
        />
      </TagsTableFrame>
    </TagsPageRoot>
  );
}
